import { CalendarClock } from 'lucide-preact';
import type { Ref } from 'preact';
import { PickerPanel } from './internal/PickerPanel.js';
import type { AllowedWindow, OccupiedWindow } from './useDateTimePicker.js';
import { useDateTimePicker } from './useDateTimePicker.js';

export interface DateTimePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  stepMinutes?: number;
  allowedWindow?: AllowedWindow | null;
  /** 205 (#292): already-committed windows, greyed out the same way allowedWindow is. */
  occupiedWindows?: OccupiedWindow[];
  /** 386 (#1301): a return picker's chosen pickup; return times are judged as the whole rental from it. */
  rangeStart?: Date;
  placeholder?: string;
  error?: boolean;
}

// Public single date+time picker. Replaces native `<input type="datetime-local">`;
// 30-min granularity, optional min/max + allowedWindow, carii app design tokens.
export function DateTimePicker({
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  stepMinutes,
  allowedWindow,
  occupiedWindows,
  rangeStart,
  placeholder = 'Select date & time',
  error,
}: DateTimePickerProps) {
  const picker = useDateTimePicker({
    value,
    onChange,
    minDate,
    maxDate,
    stepMinutes,
    allowedWindow,
    occupiedWindows,
    rangeStart,
  });

  return (
    <div className="relative w-full">
      <button
        type="button"
        data-testid="datetime-trigger"
        ref={picker.triggerRef as unknown as Ref<HTMLButtonElement>}
        disabled={disabled}
        onClick={picker.open}
        className={[
          'w-full flex items-center gap-2 px-4 py-3 rounded-lg text-left text-sm',
          'bg-surface-mid text-text cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
          'border',
          error ? 'border-error' : 'border-transparent',
        ].join(' ')}
      >
        <CalendarClock size={16} className="text-text-muted shrink-0" />
        <span className={picker.displayValue ? 'text-text' : 'text-text-muted'}>
          {picker.displayValue || placeholder}
        </span>
      </button>

      {picker.isOpen && (
        <PickerPanel
          panelRef={picker.panelRef}
          triggerRef={picker.triggerRef}
          viewYear={picker.viewYear}
          viewMonth={picker.viewMonth}
          pendingYMD={picker.pendingYMD}
          pendingHour={picker.pendingHour}
          pendingMinute={picker.pendingMinute}
          timeSlots={picker.timeSlots}
          minDate={minDate}
          maxDate={maxDate}
          onSelectDay={picker.setPendingYMD}
          onPrevMonth={picker.prevMonth}
          onNextMonth={picker.nextMonth}
          onSelectTime={(h, m) => {
            picker.setPendingHour(h);
            picker.setPendingMinute(m);
          }}
          canConfirm={picker.canConfirm}
          onConfirm={picker.confirm}
        />
      )}
    </div>
  );
}
